import { Badge, Card, Group, Stack, Text } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useSnapshot } from "valtio";
import { assistantLogDetailTexts } from "@/locales/assistant-log-detail";
import { i18nStore } from "@/store/i18n";
import type { AssistantLogConversationCardDto } from "@/types/ai-assistant-log";
import {
	formatLogCount,
	formatLogDate,
	formatLogTime,
} from "./log-format.logic";
import { LogVendorBadge } from "./log-vendor-badge";

/**
 * Satu kartu percakapan (lapis 2): judul, waktu & jumlah pesan, lencana model
 * yang menjawab (bisa lebih dari satu bila sempat failover), penanda suara,
 * dan jumlah error. Kartu bisa diklik untuk membuka transkrip (lapis 3).
 */
export function LogConversationCard({
	userId,
	conversation,
}: {
	userId: string;
	conversation: AssistantLogConversationCardDto;
}) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const td = assistantLogDetailTexts[lang];
	const navigate = useNavigate();

	return (
		<Card
			withBorder
			radius="md"
			p="md"
			style={{ cursor: "pointer" }}
			onClick={() =>
				navigate({
					to: "/admin/ai-assistant/log/$userId/$conversationId",
					params: { userId, conversationId: conversation.id },
				})
			}
			aria-label={td.openConversation}
			data-log-conversation-card
		>
			<Stack gap="xs">
				<Group justify="space-between" wrap="nowrap" align="flex-start">
					<Text fw={600} lineClamp={2}>
						{conversation.title}
					</Text>
					<Group gap="xs" wrap="nowrap">
						{conversation.hasVoice && (
							<Badge color="grape" variant="light" size="sm">
								{td.conversationVoice}
							</Badge>
						)}
						{conversation.errors > 0 && (
							<Badge color="red" variant="light" size="sm">
								{td.conversationErrors(
									formatLogCount(conversation.errors, lang),
								)}
							</Badge>
						)}
					</Group>
				</Group>

				<Text size="xs" c="dimmed">
					{td.conversationMeta(
						formatLogDate(conversation.startedAt, lang),
						formatLogTime(conversation.startedAt, lang),
						formatLogTime(conversation.lastMessageAt, lang),
						formatLogCount(conversation.messages, lang),
					)}
				</Text>

				{conversation.models.length > 0 && (
					<Group gap="xs" wrap="wrap">
						{conversation.models.map((use) => (
							<LogVendorBadge key={use.model ?? "unknown"} use={use} />
						))}
					</Group>
				)}
			</Stack>
		</Card>
	);
}
